import { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper, ReactRenderer } from '@tiptap/react';
import { useCallback, useRef } from 'react';
import tippy, { Instance } from 'tippy.js';

import { WorkerAttr } from '@/types/worker-attr';
import { cn } from '@/utils/classnames';

import EditWorkerAttribute from '../../components/worker-attribute/edit-worker-attribute';
import { getTagPath } from '../../utils/get-tag-path';

export default function WorkerAttributeNodeView(props: NodeViewProps) {
  const { label, fallback } = props.node.attrs as WorkerAttr;
  const path = getTagPath(props.node.attrs as WorkerAttr);
  const nodeRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<Instance[] | null>(null);

  const hasFallback = !!fallback;

  const onChange = useCallback(
    (newAttr: Partial<WorkerAttr>) => {
      queueMicrotask(() => props.updateAttributes({ ...newAttr }));
    },
    [props],
  );

  const handleOnClick = useCallback(() => {
    if (!nodeRef.current) {
      return;
    }

    // Close existing popup if open (toggle behavior)
    if (popupRef.current?.[0] && popupRef.current[0].state.isVisible) {
      popupRef.current[0].hide();
      popupRef.current[0].destroy();
      popupRef.current = null;
      return;
    }

    const boundingClientRect = nodeRef.current.getBoundingClientRect();

    const component = new ReactRenderer(EditWorkerAttribute, {
      props: {
        path,
        onChange,
        ...(props.node.attrs as WorkerAttr),
      },
      editor: props.editor,
    });

    // Wait for component to be fully rendered before creating tippy
    let tippyInstance: Instance[] | null = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        tippyInstance = tippy('body', {
          getReferenceClientRect: () => boundingClientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          arrow: false,
          offset: [0, 8],
          zIndex: 10001, // Higher than Sheet modal (usually 10000)
          popperOptions: {
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'viewport',
                },
              },
            ],
          },
          onClickOutside: (instance, event) => {
            const target = event.target as HTMLElement;
            if (!target) return true;

            // Don't hide if clicking inside the edit component
            if (target.closest('[data-worker-attr-edit="true"]')) {
              return false; // Don't hide
            }

            const tippyBox = instance.popper;
            if (!tippyBox) return true;

            // Check if click is inside tippy box
            if (tippyBox.contains(target)) {
              return false; // Don't hide
            }

            // Use composedPath as additional check
            const path = event.composedPath ? event.composedPath() : [];
            if (path.includes(tippyBox)) {
              return false; // Don't hide
            }

            // Check component element
            const componentElement = component.element;
            if (componentElement) {
              if (
                componentElement.contains(target) ||
                path.includes(componentElement)
              ) {
                return false; // Don't hide
              }
            }

            // Click is outside tippy - allow hiding
            return true;
          },
          onHide: () => {
            component.destroy();
            popupRef.current = null;
          },
        });

        popupRef.current = tippyInstance;
      });
    });
  }, [onChange, path, props]);

  return (
    <NodeViewWrapper
      onClick={handleOnClick}
      ref={nodeRef}
      className="react-component inline-block"
      as="span"
    >
      <span
        className={cn(
          'mx-[3px] cursor-pointer rounded-md px-[4px] py-[1px] text-sm',
          {
            'bg-blue-50 text-blue-700': hasFallback,
            'bg-amber-50 text-amber-600': !hasFallback,
          },
        )}
      >
        {label}
      </span>
    </NodeViewWrapper>
  );
}
